import React, { useState } from 'react';
import { Alert, Button, Flex, InputNumber, Modal, Select, Space, Table, Tag, Tooltip, Typography, theme } from 'antd';
import { BranchesOutlined, LinkOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ColumnsType } from 'antd/es/table';
import {
  ApiError,
  fetchParentShops,
  refreshParentAttrs,
  searchParents,
  type ParentCandidate,
  type ParentSearchReport,
} from '../../shared/api/client';
import HintLabel from '../../shared/ui/HintLabel';
import ProductThumb from '../../shared/ui/ProductThumb';
import { isAsin } from '../../shared/lib/asin';
import { popupInside } from '../../shared/lib/popupInside';

/** Parent, к которому привязан расчёт — сохраняется в `calculation_data.parent_link`. */
export interface ParentLink {
  asin: string;
  product_id?: string | null;
  shops?: string[];
  relevance?: number | null;
  bound_at?: string | null;
  /** Свежие на момент привязки рейтинг и отзывы parent: вариация наследует именно их. */
  rating?: number | null;
  reviews?: number | null;
  data_at?: string | null;
}

interface ParentSearchProps {
  asin: string;
  value: ParentLink | null;
  /** Привязка: дублирует расчёт в карту товара parent. Нет — кнопки «Привязать» нет. */
  onBind?: (candidate: ParentCandidate) => Promise<ParentLink>;
}

const DEFAULTS = { count: 10, min_relevance: 10, rating_from: 0, reviews_from: 0, pool_limit: 100 };

/**
 * Подбор parent под расчёт (WS 15587410, п.5) — по образцу «Поиска парента» Игоря: окно
 * параметров → отчёт с кандидатами из инвентаря наших аккаунтов → «Привязать».
 */
const ParentSearch: React.FC<ParentSearchProps> = ({ asin, value, onBind }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const asinOk = isAsin(asin);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [params, setParams] = useState({ ...DEFAULTS, shops: [] as string[] });
  const [shops, setShops] = useState<string[] | null>(null);
  const [visual, setVisual] = useState<boolean | null>(null);
  const [report, setReport] = useState<ParentSearchReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [binding, setBinding] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const errorText = (e: unknown) => {
    const code = e instanceof ApiError ? e.status : undefined;
    if (code === 401 || code === 428) return t('idea_calc.parent.error_platform_only');
    if (code === 404) return t('idea_calc.parent.error_unknown_asin');
    if (code === 429) return t('idea_calc.parent.error_keepa_limit');
    return t('idea_calc.parent.error_unavailable');
  };

  const openSettings = async () => {
    setError(null);
    setSettingsOpen(true);
    if (shops === null) {
      try {
        const res = await fetchParentShops();
        setShops(res.shops);
        setVisual(res.visual);
      } catch (e) {
        setError(errorText(e));
      }
    }
  };

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await searchParents({ asin, ...params });
      setReport(res);
      setSettingsOpen(false);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  };

  /** Свежие данные кандидата из Keepa (мимо недельного кэша) — прямо в строку отчёта. */
  const refreshed = async (cand: ParentCandidate): Promise<ParentCandidate> => {
    const fresh = (await refreshParentAttrs([cand.asin]))[cand.asin];
    if (!fresh) return cand;
    const { fetched_at, ...rest } = fresh;
    const next: ParentCandidate = { ...cand, ...rest, data_at: fetched_at ?? cand.data_at };
    setReport((r) => (r ? { ...r, candidates: r.candidates.map((c) => (c.asin === cand.asin ? next : c)) } : r));
    return next;
  };

  const refresh = async (cand: ParentCandidate) => {
    setRefreshing(cand.asin);
    setError(null);
    try {
      await refreshed(cand);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setRefreshing(null);
    }
  };

  const bind = async (cand: ParentCandidate) => {
    if (!onBind) return;
    setBinding(cand.asin);
    setError(null);
    try {
      // Привязка сохраняет рейтинг и отзывы parent — берём их свежими, а не из кэша.
      await onBind(await refreshed(cand));
      setReport(null);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBinding(null);
    }
  };

  const num = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString(i18n.language));

  const columns: ColumnsType<ParentCandidate> = [
    { key: 'img', title: t('idea_calc.parent.col_photo'), width: 64, render: (_, c) => <ProductThumb src={c.image ?? undefined} size={44} alt={c.asin} /> },
    {
      key: 'asin',
      title: 'ASIN',
      width: 120,
      render: (_, c) => (
        <a href={`https://www.amazon.com/dp/${c.asin}`} target="_blank" rel="noopener noreferrer">
          {c.asin}
        </a>
      ),
    },
    { key: 'rel', title: <HintLabel hint={t('idea_calc.parent.tips.relevance')}>{t('idea_calc.parent.col_relevance')}</HintLabel>, width: 90, align: 'right', render: (_, c) => <Typography.Text strong>{c.relevance}%</Typography.Text> },
    { key: 'match', title: t('idea_calc.parent.col_match'), width: 100, render: (_, c) => <Tag color={c.match === 'visual' ? 'blue' : 'default'}>{t(`idea_calc.parent.match.${c.match}`)}</Tag> },
    { key: 'title', title: t('idea_calc.parent.col_title'), ellipsis: true, render: (_, c) => <Tooltip title={c.title}>{c.title ?? '—'}</Tooltip> },
    { key: 'brand', title: t('idea_calc.parent.col_brand'), width: 110, ellipsis: true, render: (_, c) => c.brand ?? '—' },
    { key: 'shops', title: t('idea_calc.parent.col_account'), width: 140, render: (_, c) => (c.shops.length ? c.shops.join(', ') : '—') },
    { key: 'rating', title: t('idea_calc.parent.col_rating'), width: 80, align: 'right', render: (_, c) => (c.rating == null ? '—' : `${c.rating}★`) },
    { key: 'reviews', title: t('idea_calc.parent.col_reviews'), width: 90, align: 'right', render: (_, c) => num(c.reviews) },
    { key: 'cat', title: t('idea_calc.parent.col_category'), width: 170, ellipsis: true, render: (_, c) => c.categories.slice(0, 2).join(' / ') || '—' },
    { key: 'type', title: 'Type / Item type', width: 180, ellipsis: true, render: (_, c) => [c.type, c.item_type].filter(Boolean).join(' / ') || '—' },
    {
      key: 'data_at',
      title: <HintLabel hint={t('idea_calc.parent.tips.data_at')}>{t('idea_calc.parent.col_data_at')}</HintLabel>,
      width: 130,
      render: (_, c) => (
        <Flex align="center" gap={4}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {c.data_at ? new Date(c.data_at).toLocaleDateString(i18n.language) : '—'}
          </Typography.Text>
          <Tooltip title={t('idea_calc.parent.refresh')}>
            <Button size="small" type="text" icon={<ReloadOutlined />} loading={refreshing === c.asin} aria-label={t('idea_calc.parent.refresh')} onClick={() => void refresh(c)} />
          </Tooltip>
        </Flex>
      ),
    },
    { key: 'why', title: t('idea_calc.parent.col_reason'), width: 220, render: (_, c) => <Typography.Text type="secondary" style={{ fontSize: 12 }}>{c.reasons.join('; ') || '—'}</Typography.Text> },
    {
      key: 'bind',
      fixed: 'right',
      width: 120,
      render: (_, c) =>
        onBind ? (
          <Tooltip title={c.product_id ? undefined : t('idea_calc.parent.no_card')}>
            <Button size="small" type="primary" icon={<LinkOutlined />} disabled={!c.product_id} loading={binding === c.asin} onClick={() => void bind(c)}>
              {t('idea_calc.parent.bind')}
            </Button>
          </Tooltip>
        ) : null,
    },
  ];

  const best = report?.candidates[0];

  return (
    <div data-testid="parent-search">
      <Flex align="center" gap="small" wrap>
        <Typography.Text type="secondary">
          <HintLabel hint={t('idea_calc.parent.tips.label')}>{t('idea_calc.parent.label')}</HintLabel>
        </Typography.Text>
        {value && (
          <Tag color="purple" style={{ marginInlineEnd: 0 }}>
            {value.asin}
            {value.shops?.length ? ` · ${value.shops.join(', ')}` : ''}
            {value.relevance != null ? ` · ${value.relevance}%` : ''}
          </Tag>
        )}
        <Tooltip title={asinOk ? undefined : t('idea_calc.parent.need_asin')}>
          <Button size="small" icon={<BranchesOutlined />} disabled={!asinOk} onClick={() => void openSettings()}>
            {value ? t('idea_calc.parent.search_again') : t('idea_calc.parent.search_button')}
          </Button>
        </Tooltip>
      </Flex>
      {error && !settingsOpen && <Typography.Text type="danger" style={{ fontSize: 12 }}>{error}</Typography.Text>}

      <Modal
        open={settingsOpen}
        title={t('idea_calc.parent.settings_title')}
        onCancel={() => setSettingsOpen(false)}
        footer={
          <Space>
            <Button onClick={() => setSettingsOpen(false)}>{t('idea_calc.parent.cancel')}</Button>
            <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={() => void run()}>
              {t('idea_calc.parent.run')}
            </Button>
          </Space>
        }
        destroyOnClose={false}
      >
        <Flex vertical gap="middle">
          <Flex gap="middle" wrap>
            <Flex vertical gap={4}>
              <Typography.Text type="secondary">{t('idea_calc.parent.param_count')}</Typography.Text>
              <InputNumber min={1} max={50} value={params.count} onChange={(v) => setParams({ ...params, count: v ?? DEFAULTS.count })} />
            </Flex>
            <Flex vertical gap={4}>
              <Typography.Text type="secondary">{t('idea_calc.parent.param_min_relevance')}</Typography.Text>
              <InputNumber min={0} max={100} value={params.min_relevance} onChange={(v) => setParams({ ...params, min_relevance: v ?? 0 })} />
            </Flex>
            <Flex vertical gap={4}>
              <Typography.Text type="secondary">{t('idea_calc.parent.param_rating_from')}</Typography.Text>
              <InputNumber min={0} max={5} step={0.1} value={params.rating_from} onChange={(v) => setParams({ ...params, rating_from: v ?? 0 })} />
            </Flex>
            <Flex vertical gap={4}>
              <Typography.Text type="secondary">{t('idea_calc.parent.param_reviews_from')}</Typography.Text>
              <InputNumber min={0} value={params.reviews_from} onChange={(v) => setParams({ ...params, reviews_from: v ?? 0 })} />
            </Flex>
            <Flex vertical gap={4}>
              <Typography.Text type="secondary"><HintLabel hint={t('idea_calc.parent.tips.pool')}>{t('idea_calc.parent.param_pool')}</HintLabel></Typography.Text>
              <InputNumber min={10} max={500} step={50} value={params.pool_limit} onChange={(v) => setParams({ ...params, pool_limit: v ?? DEFAULTS.pool_limit })} />
            </Flex>
          </Flex>
          <Flex vertical gap={4}>
            <Typography.Text type="secondary">{t('idea_calc.parent.param_accounts')}</Typography.Text>
            <Select
              mode="multiple"
              getPopupContainer={popupInside}
              allowClear
              loading={shops === null && !error}
              placeholder={t('idea_calc.parent.all_accounts')}
              value={params.shops}
              onChange={(v: string[]) => setParams({ ...params, shops: v })}
              options={(shops ?? []).map((s) => ({ value: s, label: s }))}
            />
          </Flex>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {visual === false ? t('idea_calc.parent.mode_attributes_hint') : t('idea_calc.parent.mode_combined_hint')}
          </Typography.Text>
          {error && <Alert type="error" showIcon message={error} />}
        </Flex>
      </Modal>

      <Modal open={report !== null} title={t('idea_calc.parent.report_title')} onCancel={() => setReport(null)} footer={null} width="min(1400px, 96vw)">
        {report && (
          <Flex vertical gap="middle">
            <Flex gap="middle" align="center">
              <ProductThumb src={report.target.image ?? undefined} size={56} alt={report.target.asin} />
              <Flex vertical>
                <Typography.Text strong>{report.target.title ?? report.target.asin}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {[report.target.asin, report.target.brand, report.target.categories.at(-1)].filter(Boolean).join(' · ')}
                </Typography.Text>
              </Flex>
            </Flex>
            <div style={{ padding: token.paddingSM, borderRadius: token.borderRadius, background: token.colorFillQuaternary }}>
              <Typography.Text>
                {t('idea_calc.parent.summary', { found: report.candidates.length, pool: report.pool_size })}
                {best ? ` ${t('idea_calc.parent.summary_best', { asin: best.asin, relevance: best.relevance, shops: best.shops.join(', ') || '—' })}` : ''}
              </Typography.Text>
              {report.accounts.length > 0 && (
                <>
                  <Typography.Text strong style={{ display: 'block', marginTop: 6 }}>{t('idea_calc.parent.accounts_title')}</Typography.Text>
                  <ul style={{ margin: '4px 0 0', paddingInlineStart: 20 }}>
                    {report.accounts.map((a) => (
                      <li key={a.shop}>{t('idea_calc.parent.account_line', { shop: a.shop, parents: a.parents, best: a.best })}</li>
                    ))}
                  </ul>
                </>
              )}
              <Typography.Text type="warning" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>{t('idea_calc.parent.reviews_warning')}</Typography.Text>
            </div>
            {report.mode === 'attributes' && <Alert type="info" showIcon message={t('idea_calc.parent.mode_attributes_alert')} />}
            {error && <Alert type="error" showIcon message={error} />}
            <Table<ParentCandidate>
              size="small"
              rowKey="asin"
              columns={columns}
              dataSource={report.candidates}
              pagination={false}
              scroll={{ x: 'max-content', y: '55vh' }}
              locale={{ emptyText: t('idea_calc.parent.empty') }}
            />
          </Flex>
        )}
      </Modal>
    </div>
  );
};

export default ParentSearch;
